import { useState, useEffect } from 'react';

function Categories({ token }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', symptoms: '', solution: '', keywords: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const resp = await fetch('/api/categories', { headers: { Authorization: `Bearer ${token}` } });
      const data = await resp.json();
      setCategories(data);
    } catch (err) {
      console.error('Error cargando categorias:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const body = {
      name: form.name,
      symptoms: form.symptoms.split('\n').map(s => s.trim()).filter(Boolean),
      solution: form.solution,
      keywords: form.keywords.split(',').map(k => k.trim()).filter(Boolean)
    };

    try {
      const url = editing ? `/api/categories/${editing}` : '/api/categories';
      const method = editing ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);

      setMessage(editing ? 'Categoria actualizada' : 'Categoria creada');
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', symptoms: '', solution: '', keywords: '' });
      fetchCategories();
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const editCategory = (cat) => {
    setEditing(cat.id);
    setForm({
      name: cat.name,
      symptoms: cat.symptoms.join('\n'),
      solution: cat.solution,
      keywords: (cat.keywords || []).join(', ')
    });
    setShowForm(true);
    setMessage('');
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Eliminar esta categoria?')) return;
    try {
      await fetch(`/api/categories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(categories.filter(c => c.id !== id));
    } catch (err) {
      console.error('Error eliminando:', err);
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', symptoms: '', solution: '', keywords: '' });
    setMessage('');
  };

  if (loading) {
    return <div className="categories-page"><div className="spinner" style={{ margin: '3rem auto' }}></div></div>;
  }

  return (
    <div className="categories-page">
      <div className="categories-header">
        <div>
          <h2 className="page-title">Categorias de Problemas</h2>
          <p className="page-subtitle">Administra las categorias, sintomas y soluciones de referencia</p>
        </div>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', symptoms: '', solution: '', keywords: '' }); }}>
            + Nueva Categoria
          </button>
        )}
      </div>

      {message && (
        <div className={`config-message ${message.startsWith('Error') ? 'error' : 'success'}`}>{message}</div>
      )}

      {showForm && (
        <div className="category-form-card">
          <h3>{editing ? 'Editar Categoria' : 'Nueva Categoria'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Nombre de la categoria</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: DAÑO EN DISCO DURO" required />
            </div>
            <div className="form-group">
              <label>Sintomas (uno por linea)</label>
              <textarea rows="5" value={form.symptoms} onChange={e => setForm({ ...form, symptoms: e.target.value })}
                placeholder={"sonidos en el disco duro\ncalentamiento excesivo\nequipo lento al acceder archivos"} required />
            </div>
            <div className="form-group">
              <label>Solucion estandar</label>
              <textarea rows="3" value={form.solution} onChange={e => setForm({ ...form, solution: e.target.value })}
                placeholder="Cambio de disco duro. Realizar backup previo si es posible." required />
            </div>
            <div className="form-group">
              <label>Palabras clave (separadas por coma)</label>
              <input type="text" value={form.keywords} onChange={e => setForm({ ...form, keywords: e.target.value })}
                placeholder="disco duro, hdd, ssd, sonido, caliente" />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Categoria')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={cancelForm}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      <div className="categories-grid">
        {categories.map(cat => (
          <div key={cat.id} className="category-card-item">
            <div className="cat-card-header">
              <h3>{cat.name}</h3>
              <div className="cat-card-actions">
                <button className="icon-btn" onClick={() => editCategory(cat)} title="Editar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button className="icon-btn icon-btn-danger" onClick={() => deleteCategory(cat.id)} title="Eliminar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
            <div className="cat-section">
              <label>Sintomas</label>
              <div className="tags-list">
                {cat.symptoms.map((s, i) => <span key={i} className="tag symptom-tag">{s}</span>)}
              </div>
            </div>
            <div className="cat-section">
              <label>Solucion</label>
              <p>{cat.solution}</p>
            </div>
            {cat.keywords && cat.keywords.length > 0 && (
              <div className="cat-section">
                <label>Palabras clave</label>
                <div className="tags-list">
                  {cat.keywords.map((k, i) => <span key={i} className="tag keyword-tag">{k}</span>)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Categories;
